import { z } from 'zod';
import { entity, EntityTypeSchema, IdSchema, IsoDateTimeSchema } from './common';

export const FileReferenceSchema = entity('FileReference', {
  clientId: IdSchema.optional(),
  sessionId: IdSchema.optional(),
  type: z.enum(['audio', 'transcript', 'document', 'backup']),
  storage: z.enum(['local', 'google-drive']),
  localPath: z.string().optional(),
  driveFileId: z.string().optional(),
  fileName: z.string().min(1),
  mimeType: z.string().optional(),
  sizeBytes: z.number().int().min(0).optional(),
  encrypted: z.boolean(),
  checksum: z.string().optional(),
});
export type FileReference = z.infer<typeof FileReferenceSchema>;

export const AIImportSchema = entity('AIImport', {
  clientId: IdSchema,
  sessionId: IdSchema.optional(),
  provider: z.enum(['chatgpt', 'claude', 'other']),
  /** Amendment A24: report-draft (사례·종결보고서 서술 초안) */
  purpose: z.enum(['session-analysis', 'second-review', 'case-formulation', 'next-session', 'report-draft', 'other']),
  /** Amendment A24: 서술 초안을 넣을 문서 */
  documentId: IdSchema.optional(),
  packetVersion: z.string(),
  outputFormatVersion: z.string().optional(),
  promptVersion: z.string(),
  rawInputSnapshot: z.string().optional(),
  /** 파싱 실패해도 반드시 보존 (AI_PROTOCOL §13) */
  rawOutput: z.string(),
  parseStatus: z.enum(['pending', 'parsed', 'partial', 'failed']),
  parseErrors: z.array(z.string()).optional(),
  /** Amendment A23: 반영 완료 시각 (재반영 방지) */
  appliedAt: IsoDateTimeSchema.optional(),
  parseWarnings: z.array(z.string()).optional(),
  roleReviewProposals: z
    .array(
      z
        .object({
          segmentId: z.string(),
          proposedRole: z.enum(['counselor', 'client']),
          reason: z.string().optional(),
        })
        .strict(),
    )
    .optional(),
});
export type AIImport = z.infer<typeof AIImportSchema>;

export const AuditActionSchema = z.enum([
  'create',
  'update',
  'approve',
  'revise',
  'delete',
  'restore',
  'sync',
  'resolve-conflict',
  'migrate',
  'backup',
  'restore-backup',
]);

export const AuditEventSchema = entity('AuditEvent', {
  clientId: IdSchema.optional(),
  sessionId: IdSchema.optional(),
  targetEntityType: EntityTypeSchema,
  targetEntityId: IdSchema,
  action: AuditActionSchema,
  /** 고정 코드만 (예: "journal.approved"). 임상 내용·이름 금지 (Amendment A17) */
  summary: z
    .string()
    .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9-]+)*$/, 'summary는 코드 형식만 허용')
    .optional(),
  previousRevision: z.number().int().min(0).optional(),
  newRevision: z.number().int().min(1).optional(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const SyncStateSchema = entity('SyncState', {
  targetEntityType: EntityTypeSchema,
  targetEntityId: IdSchema,
  localRevision: z.number().int().min(1),
  remoteRevision: z.number().int().min(1).optional(),
  baseContentHash: z.string().optional(),
  syncStatus: z.enum(['local-only', 'synced', 'pending', 'conflict', 'error']),
  lastSyncedAt: IsoDateTimeSchema.optional(),
  conflictId: IdSchema.optional(),
});
export type SyncState = z.infer<typeof SyncStateSchema>;

export const SyncConflictSchema = entity('SyncConflict', {
  targetEntityType: EntityTypeSchema,
  targetEntityId: IdSchema,
  localRevision: z.number().int().min(1),
  remoteRevision: z.number().int().min(1),
  localPayload: z.unknown(),
  remotePayload: z.unknown(),
  /** Amendment A20: 동기화 판정용 지문과 출처 기기 */
  localHash: z.string().optional(),
  remoteHash: z.string().optional(),
  remoteDeviceId: z.string().optional(),
  reason: z.enum(['same-field', 'approved-record', 'no-common-base', 'invalid-change']).optional(),
  resolution: z.enum(['unresolved', 'local', 'remote', 'merged']),
  resolvedAt: IsoDateTimeSchema.optional(),
});
export type SyncConflict = z.infer<typeof SyncConflictSchema>;

export const AppSettingsSchema = entity('AppSettings', {
  autoLockMinutes: z.number().int().min(1).max(120),
  defaultSessionMinutes: z.number().int().min(1),
  audioPolicy: z.enum(['local-only', 'drive-encrypted', 'manual']),
  promptVersions: z
    .object({
      chatgptInstruction: z.string(),
      claudeInstruction: z.string(),
      sessionPacket: z.string(),
      sessionAnalysis: z.string(),
    })
    .strict(),
  backupReminderDays: z.number().int().min(1),
  lastBackupAt: IsoDateTimeSchema.optional(),
  /** Amendment A25: 상담자 정보 (문서의 상담자·발표자 칸, 기본 장소). 모든 기기에서 같도록 동기화 */
  counselorName: z.string().max(40).optional(),
  organization: z.string().max(80).optional(),
  defaultLocation: z.string().max(100).optional(),
  /** Amendment A30: 지도감독 원칙·작성 기준 (비우면 앱 기본값). 보고서 요청에 매번 함께 보낸다 */
  writingGuidelines: z.string().max(20000).optional(),
  /** Amendment A35: 심리검사 해석용 외부 AI 프로젝트 주소 (해설 자료를 올려 둔 곳) */
  assessmentProjectUrl: z.string().url().startsWith('https://').max(500).optional(),
  /** Amendment A42: 문장완성검사 문항 목록 (한 번 넣어 두면 옮겨 적기 기준으로 쓴다) */
  sctStems: z.array(z.string().max(500)).max(500).optional(),
});
export type AppSettings = z.infer<typeof AppSettingsSchema>;

export const GlossaryTermSchema = entity('GlossaryTerm', {
  term: z.string().min(1),
  variants: z.array(z.string()).optional(),
  category: z.enum(['faith', 'clinical', 'theory', 'person-role', 'other']).optional(),
});
export type GlossaryTerm = z.infer<typeof GlossaryTermSchema>;
